import { useState, useEffect, useCallback, useRef } from "react";
import { agentTenderService } from "../../services/agentTenderApi";
import {
  DEFAULT_FEED_FILTERS,
  CATEGORY_PLAYERS,
} from "../../constants/agentTenderChoices";
import { useDebounce } from "../useDebounce";
import { useMediaQuery } from "../useMediaQuery";
import { useViewMode } from "../useViewMode";

const DEFAULT_FILTERS = DEFAULT_FEED_FILTERS;

// Filter keys whose non-empty value counts as an active filter (drives the
// "Clear all" affordance). "status" is excluded because "all" is its neutral.
const ACTIVE_FILTER_KEYS = [
  "tender_category",
  "market_value_from",
  "market_value_to",
  "continent",
  "country",
  "division",
  "position",
  "nationality",
  "min_age",
  "max_age",
  "type_of_tender",
  "recruiting_for",
  "sort_by",
  "verified",
];

// Feed state for /agent-tenders. Verified-first ordering comes from the
// backend serializer; this hook never re-sorts. Mirrors the club useClubTenders
// shape (filters + pagination + debounced search) so the two feel identical.
export const useAgentTenderFeed = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [viewMode, changeViewMode] = useViewMode("agentTendersViewMode");

  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  // Feed category pill (Players / Coaches / Staffs / Others). Players is the
  // default bucket. tabCounts come from the list response (keyed by pill value)
  // and drive the pill badges.
  const [activeCategory, setActiveCategory] = useState(CATEGORY_PLAYERS);
  const [tabCounts, setTabCounts] = useState({
    players: 0,
    coaches: 0,
    staffs: 0,
    others: 0,
  });
  // Saved filter (P6): the anonymised feed narrowed to the user's saved
  // requests (?saved=true). savingIds tracks in-flight save/unsave toggles.
  const [savedOnly, setSavedOnly] = useState(false);
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  const abortRef = useRef(null);

  const buildParams = useCallback(
    (page) => {
      const p = new URLSearchParams();
      p.append("page", page);
      // Full club-parity param set (G4). Only append meaningful values; "all"
      // and empty are the neutral states and are omitted.
      const passthrough = [
        "tender_category",
        "market_value_from",
        "market_value_to",
        "continent",
        "country",
        "division",
        "position",
        "nationality",
        "min_age",
        "max_age",
        "type_of_tender",
        "recruiting_for",
        "sort_by",
      ];
      passthrough.forEach((k) => {
        if (filters[k]) p.append(k, filters[k]);
      });
      if (filters.status && filters.status !== "all") p.append("status", filters.status);
      if (filters.verified) p.append("verified", "true");
      if (debouncedSearch) p.append("search", debouncedSearch);
      if (activeCategory) p.append("category", activeCategory);
      if (savedOnly) p.append("saved", "true");
      return p;
    },
    [filters, debouncedSearch, activeCategory, savedOnly],
  );

  const fetchTenders = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await agentTenderService.getFeed(buildParams(page), controller.signal);
        if (res.success) {
          setTenders(res.data);
          setPagination(res.pagination);
          if (res.tabCounts) setTabCounts(res.tabCounts);
        } else {
          setError(res.error || "Failed to load agent requests.");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load agent requests. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  useEffect(() => {
    fetchTenders(1);
  }, [fetchTenders]);

  const applyFilters = (next) => {
    setFilters(next);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const changeCategory = (category) => {
    if (category === activeCategory) return;
    setActiveCategory(category);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const toggleSavedOnly = () => {
    setSavedOnly((v) => !v);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Explicit setter for the Saved top-tab (All Listed → false, Saved → true).
  const showSaved = (on) => {
    setSavedOnly(!!on);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Optimistic save/unsave. Flips is_saved immediately, calls the endpoint, and
  // reverts on failure. In the Saved view an unsave drops the row. Idempotent
  // (the backend endpoints are DB-unique), guarded per-id against double taps.
  const toggleSave = useCallback(
    async (tender) => {
      const id = tender.id;
      if (savingIds.has(id)) return;
      const wasSaved = !!tender.is_saved;
      setSavingIds((prev) => new Set(prev).add(id));
      setTenders((prev) =>
        prev.map((t) => (t.id === id ? { ...t, is_saved: !wasSaved } : t)),
      );
      const res = wasSaved
        ? await agentTenderService.unsave(id)
        : await agentTenderService.save(id);
      if (!res.success) {
        setTenders((prev) =>
          prev.map((t) => (t.id === id ? { ...t, is_saved: wasSaved } : t)),
        );
      } else if (savedOnly && wasSaved) {
        setTenders((prev) => prev.filter((t) => t.id !== id));
      }
      setSavingIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    },
    [savingIds, savedOnly],
  );

  const toggleVerifiedOnly = () =>
    applyFilters({ ...filters, verified: !filters.verified });

  const hasActiveFilters = ACTIVE_FILTER_KEYS.some((k) => !!filters[k]);

  const clearFilters = () => applyFilters(DEFAULT_FILTERS);

  return {
    isMobile,
    viewMode,
    changeViewMode,
    tenders,
    loading,
    error,
    showFilterModal,
    setShowFilterModal,
    filters,
    applyFilters,
    activeCategory,
    changeCategory,
    tabCounts,
    savedOnly,
    toggleSavedOnly,
    showSaved,
    toggleSave,
    savingIds,
    toggleVerifiedOnly,
    hasActiveFilters,
    clearFilters,
    searchTerm,
    setSearchTerm,
    pagination,
    fetchTenders,
  };
};
